import React, { useState, useRef, useEffect } from 'react'
import { Plus, FileUp, Upload, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { supabase } from '@/integrations/supabase/client'
import * as XLSX from 'xlsx'
import { API_BASE_URL } from '@/utils/apiConfig'
import { toast } from '@/hooks/use-toast'

const EmployeeAddModal = ({ open, setOpen, onEmployeeAdded, onEmployeesImported }) => {
  const [tab, setTab] = useState('manual')
  const [addForm, setAddForm] = useState({
    name: '',
    email: '',
    department: '',
    role: '',
    sicil: '',
    kod: '',
  })
  const [addError, setAddError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [importError, setImportError] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const fileInputRef = useRef()

  useEffect(() => {
    if (!open) {
      setAddForm({
        name: '',
        email: '',
        department: '',
        role: '',
        sicil: '',
        kod: '',
      })
      setAddError(null)
      setImportError(null)
      setImportResult(null)
      setUploading(false)
      setTab('manual')
    }
  }, [open])

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // MANUAL
  const handleAdd = async () => {
    setAddError(null)
    if (!addForm.name || !addForm.email) {
      setAddError('Ad Soyad ve E-posta zorunludur.')
      return
    }
    if (!isValidEmail(addForm.email)) {
      setAddError('Geçerli bir e-posta adresi giriniz (örn: kullanici@sirket.com)')
      return
    }

    // Trim and normalize sicil
    const normalizedSicil = addForm.sicil.trim()

    if (!normalizedSicil) {
      setAddError('Sicil numarası zorunludur.')
      return
    }

    // "sicil" control with trimmed value
    const { data: exists, error: checkError } = await supabase
      .from('employee')
      .select('id')
      .eq('sicil', normalizedSicil)

    // Check if exists array has any records
    if (exists && exists.length > 0) {
      setAddError('Bu sicil numarası ile kayıtlı bir çalışan zaten var.')
      return
    }

    // Add employee with normalized sicil
    const { data, error } = await supabase
      .from('employee')
      .insert([
        {
          personel: addForm.name.trim(),
          mail_adresi: addForm.email.trim(),
          magaza: addForm.department.trim(),
          pozisyon: addForm.role.trim(),
          sicil: normalizedSicil,
          kod: addForm.kod.trim(),
        },
      ])
      .select()
      .single()

    if (error) {
      setAddError('Kayıt sırasında hata oluştu: ' + error.message)
      return
    }

    onEmployeeAdded &&
      onEmployeeAdded({
        id: data.id,
        name: data.personel,
        email: data.mail_adresi,
        department: data.magaza || '',
        role: data.pozisyon || '',
        createdAt: data.created_at || new Date().toISOString(),
        sicil: data.sicil,
        kod: data.kod,
        magaza: data.magaza,
      })
    setOpen(false)
  }

  // EXCEL IMPORT

  const handleFile = async (file) => {
    setImportError(null)
    setImportResult(null)

    // Only accept Excel or CSV
    if (
      !(
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv'
      )
    ) {
      setImportError('Yalnızca .xlsx, .xls veya .csv dosyası seçebilirsiniz.')
      return
    }

    setUploading(true)

    try {
      // Read the file as ArrayBuffer for XLSX
      const data = await file.arrayBuffer()
      const workbook = XLSX.read(data, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      const sheet = workbook.Sheets[sheetName]
      const rows = XLSX.utils.sheet_to_json(sheet)
      console.log('[EmployeeImport] File loaded, rows:', rows.length)

      let inserted = []
      let failed = []

      // Check for duplicates within the Excel file itself
      const excelSicils = rows.map((row) => (row['sicil'] || '').toString().trim()).filter((s) => s)

      const duplicatesInExcel = excelSicils.filter(
        (sicil, index) => excelSicils.indexOf(sicil) !== index
      )

      if (duplicatesInExcel.length > 0) {
        const uniqueDuplicates = [...new Set(duplicatesInExcel)]
        setImportError(
          `Excel dosyasında tekrarlanan sicil numaraları bulundu: ${uniqueDuplicates.join(', ')}`
        )
        setUploading(false)
        return
      }

      // Batch check all sicils against database, chunked to keep URL length
      // under proxy limits (Cloudflare/PostgREST reject very long URLs).
      const BATCH_SIZE = 200
      const existingSicils = new Set()
      for (let i = 0; i < excelSicils.length; i += BATCH_SIZE) {
        const batch = excelSicils.slice(i, i + BATCH_SIZE)
        const { data: existingEmployees, error: batchCheckError } = await supabase
          .from('employee')
          .select('sicil')
          .in('sicil', batch)

        if (batchCheckError) {
          setImportError('Veritabanı kontrolünde hata: ' + batchCheckError.message)
          setUploading(false)
          return
        }

        existingEmployees?.forEach((e) => existingSicils.add(e.sicil))
      }

      // Process rows - skip those already in DB
      for (const row of rows) {
        const sicil = (row['sicil'] || '').toString().trim()
        const personel = (row['personel'] || '').toString().trim()
        const mail_adresi = (row['mail_adresi'] || '').toString().trim()
        const kod = (row['kod'] || '').toString().trim()
        const magaza = (row['magaza'] || '').toString().trim()
        const pozisyon = (row['pozisyon'] || '').toString().trim()

        // Skip if sicil, personel or mail_adresi is missing
        if (!sicil || !personel || !mail_adresi) {
          failed.push({
            sicil,
            personel,
            mail_adresi,
            reason: 'Eksik sicil, ad veya e-posta.',
          })
          continue
        }

        // Check against pre-fetched existing sicils
        if (existingSicils.has(sicil)) {
          failed.push({
            sicil,
            personel,
            mail_adresi,
            reason: 'Bu sicil numarasına sahip çalışan zaten mevcut.',
          })
          continue
        }

        // Insert employee if not exists
        const { data: insertedData, error: insertError } = await supabase
          .from('employee')
          .insert([
            {
              sicil,
              personel,
              mail_adresi,
              kod,
              magaza,
              pozisyon,
            },
          ])
          .select()
          .single()

        if (insertError) {
          // Check if it's a unique constraint violation
          if (
            insertError.code === '23505' ||
            insertError.message.includes('duplicate') ||
            insertError.message.includes('unique')
          ) {
            failed.push({
              sicil,
              personel,
              mail_adresi,
              reason: `Sicil numarası ${sicil} zaten mevcut`,
            })
          } else {
            failed.push({
              sicil,
              personel,
              mail_adresi,
              reason: 'Supabase hatası: ' + insertError.message,
            })
          }
          continue
        }

        existingSicils.add(sicil)
        inserted.push(insertedData)
      }

      setImportResult({
        inserted: inserted.length,
        failed: failed.length,
        failedRows: failed,
      })

      if (inserted.length > 0) {
        onEmployeesImported && onEmployeesImported(inserted)
      }

      // Show toast for failed records (if any)
      if (failed.length > 0) {
        // Limit how many to show in toast
        const msg =
          failed
            .slice(0, 5)
            .map((f) => `• ${f.name || '-'} (${f.sicil || '-'}) - ${f.reason || 'Bilinmeyen hata'}`)
            .join('\n') +
          (failed.length > 5 ? `\n...ve ${failed.length - 5} çalışana ekleme yapılamadı.` : '')

        toast({
          title: 'Bazı çalışanlar eklenemedi!',
          description: msg,
          variant: 'destructive',
          duration: 12000,
        })
      } else {
        toast({
          title: 'İçe aktarma tamamlandı!',
          description: `${inserted.length} çalışan başarıyla eklendi.`,
          duration: 6000,
        })
      }
    } catch (err) {
      setImportError('Dosya okunamadı veya veri aktarılırken hata oluştu.')
    } finally {
      setUploading(false)
    }
  }

  function downloadEmployeeExcelTemplate() {
    const headers = [
      'sicil', // Sicil No
      'personel', // Ad Soyad
      'mail_adresi', // E-posta
      'kod', // Kod
      'magaza', // Mağaza/Bölüm
      'pozisyon', // Pozisyon
    ]

    const exampleRow = [
      '12345',
      'Ahmet Eryılmaz',
      'ahmet@example.com',
      '1111',
      '1501 - IST AVM',
      'SATIŞ DANIŞMANI',
    ]

    const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Çalışanlar')

    XLSX.writeFile(wb, 'calisan_sablon.xlsx')
  }

  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0])
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Yeni Çalışan Ekle</DialogTitle>
          <DialogDescription>
            Manuel olarak çalışan bilgilerini girin veya Excel dosyası yükleyin.
          </DialogDescription>
        </DialogHeader>
        {/* Tab */}
        <div className="flex mt-2 mb-4 gap-1">
          <button
            className={`flex-1 px-3 py-2 rounded-md font-medium flex items-center gap-2 justify-center ${
              tab === 'manual'
                ? 'bg-gray-100 text-black'
                : 'bg-white text-gray-500 border border-gray-200'
            }`}
            onClick={() => setTab('manual')}
            type="button"
          >
            <Plus className="inline-block w-4 h-4 mr-1" />
            Manuel Giriş
          </button>
          <button
            className={`flex-1 px-3 py-2 rounded-md font-medium flex items-center gap-2 justify-center ${
              tab === 'excel'
                ? 'bg-gray-100 text-black'
                : 'bg-white text-gray-500 border border-gray-200'
            }`}
            onClick={() => setTab('excel')}
            type="button"
          >
            <FileUp className="inline-block w-4 h-4 mr-1" />
            Excel İçe Aktar
          </button>
        </div>

        {/* MANUAL */}
        {tab === 'manual' && (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              handleAdd()
            }}
          >
            <div>
              <label className="text-sm font-medium">
                Ad Soyad <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Çalışan adı ve soyadı"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">
                E-posta <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder="ornek@sirket.com"
                value={addForm.email}
                onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Mağaza/Bölüm</label>
              <Input
                placeholder="Mağaza veya bölüm adı"
                value={addForm.department}
                onChange={(e) => setAddForm((f) => ({ ...f, department: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Pozisyon</label>
              <Input
                placeholder="Çalışan pozisyonu"
                value={addForm.role}
                onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-sm font-medium">
                  Sicil No <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Sicil numarası"
                  value={addForm.sicil}
                  onChange={(e) => setAddForm((f) => ({ ...f, sicil: e.target.value }))}
                />
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium">Kod</label>
                <Input
                  placeholder="Mağaza kodu"
                  value={addForm.kod}
                  onChange={(e) => setAddForm((f) => ({ ...f, kod: e.target.value }))}
                />
              </div>
            </div>
            {addError && <div className="text-red-500 text-sm mt-1">{addError}</div>}
            <DialogFooter className="mt-4">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Ekleniyor...
                  </>
                ) : (
                  'Çalışan Ekle'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* EXCEL */}
        {tab === 'excel' && (
          <div>
            <div
              className="mb-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center py-8 cursor-pointer transition-all border-gray-200 bg-gray-50"
              onClick={() => !uploading && fileInputRef.current && fileInputRef.current.click()}
              style={{ minHeight: '140px' }}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-10 h-10 mb-2 text-blue-600 animate-spin" />
                  <span className="font-medium text-blue-600">Yükleniyor...</span>
                  <div className="text-xs text-gray-400 mt-1">Lütfen bekleyin</div>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 mb-2 text-gray-400" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={onFileChange}
                    disabled={uploading}
                  />
                  <span className="font-medium text-blue-600 hover:underline">
                    Excel dosyası seçin
                  </span>
                  <div className="text-xs text-gray-400 mt-1">.xlsx, .xls veya .csv formatında</div>
                </>
              )}
            </div>
            {/* Error & Sonuç */}
            {importError && <div className="text-red-500 text-sm mb-2">{importError}</div>}
            {importResult && (
              <div className="text-green-600 text-sm mb-2">
                {importResult.inserted} çalışan başarıyla eklendi.
                {importResult.failed > 0 && (
                  <div className="text-red-500">{importResult.failed} satır eklenemedi.</div>
                )}
              </div>
            )}
            {/* Şablon info */}
            <div className="mt-2 mb-2 text-sm">
              <Button
                variant="outline"
                size="sm"
                className="mb-3"
                onClick={downloadEmployeeExcelTemplate}
              >
                Excel Şablonunu İndir
              </Button>
              <div className="font-semibold mb-1">Excel dosyası şu sütunları içermelidir:</div>
              <ul className="list-disc pl-5 text-gray-600">
                <li>
                  <b>Ad Soyad</b> <span className="text-xs text-gray-400">(zorunlu)</span>
                </li>
                <li>
                  <b>E-posta</b> <span className="text-xs text-gray-400">(zorunlu)</span>
                </li>
                <li>
                  <b>Mağaza/Bölüm</b>
                </li>
                <li>
                  <b>Pozisyon</b>
                </li>
                <li>
                  <b>Sicil No</b> <span className="text-xs text-gray-400">(zorunlu)</span>
                </li>
                <li>
                  <b>Kod</b>
                </li>
              </ul>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>
                İptal
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default EmployeeAddModal
